import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { listPrivacyRequests } from "../../api/clientVendorDirectoryPrivacyApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.privacy";

const CLOSED_STATUSES = new Set(["resolved", "rejected", "withdrawn", "closed"]);

const STATUS_FILTERS = [
  "",
  "submitted",
  "identity_check",
  "under_review",
  "awaiting_information",
  "resolved",
  "rejected",
];

function statusBadgeClass(status) {
  if (status === "resolved" || status === "approved") return "cvd-badge--published";
  if (["rejected", "withdrawn", "closed"].includes(status)) return "cvd-badge--suspended";
  if (["under_review", "awaiting_information", "identity_check"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function isOverdue(item) {
  if (!item?.dueAt) return false;
  if (CLOSED_STATUSES.has(item.status)) return false;
  return new Date(item.dueAt) < new Date();
}

function MetricCard({ label, value }) {
  return (
    <article className="cvd-metric-card">
      <span className="cvd-metric-card__label">{label}</span>
      <strong className="cvd-metric-card__value">{value ?? 0}</strong>
    </article>
  );
}

export default function ClientVendorDirectoryPrivacyListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const status = searchParams.get("status") || "";
  const overdue = searchParams.get("overdue") === "true";
  const basePath = `${serviciosBase}/privacidad`;
  const viewName = activeView?.name || "CasaMapa";

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listPrivacyRequests(clientViewId, {
        status: status || undefined,
        overdue: overdue || undefined,
      });
      setRequests(body.requests || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, overdue, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const open = requests.filter((item) => !CLOSED_STATUSES.has(item.status)).length;
    const overdueCount = requests.filter((item) => isOverdue(item)).length;
    const suppressed = requests.filter((item) => item.profileSuppressed).length;
    return { total: requests.length, open, overdue: overdueCount, suppressed };
  }, [requests]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const toggleOverdue = () => {
    updateParam("overdue", overdue ? "" : "true");
  };

  return (
    <div className="page-content cvd-admin cvd-privacy">
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`, { viewName })}</p>
        </div>
        <div className="cvd-admin__actions">
          <button type="button" className="cvd-btn cvd-btn--ghost" onClick={load}>
            {t(`${COPY_PREFIX}.refresh`)}
          </button>
          <Link className="cvd-btn cvd-btn--secondary" to={`${basePath}/retencion`}>
            {t(`${COPY_PREFIX}.retentionLink`)}
          </Link>
        </div>
      </div>

      {!loading && requests.length > 0 ? (
        <div className="cvd-metric-grid">
          <MetricCard label={t(`${COPY_PREFIX}.kpi.total`)} value={summary.total} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.open`)} value={summary.open} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.overdue`)} value={summary.overdue} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.suppressed`)} value={summary.suppressed} />
        </div>
      ) : null}

      <div className="cvd-duplicate-filters">
        <div className="cvd-duplicate-filters__group">
          <span className="cvd-duplicate-filters__label">{t(`${COPY_PREFIX}.filterStatus`)}</span>
          <div className="cvd-run-filters" role="group" aria-label={t(`${COPY_PREFIX}.filterStatus`)}>
            {STATUS_FILTERS.map((value) => (
              <button
                key={value || "all-status"}
                type="button"
                className={`cvd-run-filter-chip${status === value ? " is-active" : ""}`}
                onClick={() => updateParam("status", value)}
              >
                {value ? t(`${COPY_PREFIX}.status.${value}`) : t(`${COPY_PREFIX}.allStatuses`)}
              </button>
            ))}
          </div>
        </div>

        <div className="cvd-duplicate-filters__group">
          <div className="cvd-run-filters">
            <button
              type="button"
              className={`cvd-run-filter-chip${overdue ? " is-active" : ""}`}
              onClick={toggleOverdue}
            >
              {t(`${COPY_PREFIX}.filterOverdue`)}
            </button>
          </div>
        </div>
      </div>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      {!loading && !errorMessage && requests.length === 0 ? (
        <section className="cvd-run-empty">
          <h2>{t(`${COPY_PREFIX}.emptyTitle`)}</h2>
          <p>{t(`${COPY_PREFIX}.emptyHint`)}</p>
        </section>
      ) : null}

      {!loading && !errorMessage && requests.length > 0 ? (
        <div className="cvd-run-list">
          {requests.map((item) => (
            <article key={item.id} className="cvd-run-card cvd-privacy-card">
              <div className="cvd-run-card__main">
                <div className="cvd-run-card__head">
                  <div>
                    <h2 className="cvd-privacy-card__reference">{item.trackingReference || "—"}</h2>
                    <p className="cvd-run-card__sub">
                      {item.vendorName || item.vendorId} ·{" "}
                      {t(`${COPY_PREFIX}.requestType.${item.requestType}`, {}, item.requestType)}
                    </p>
                  </div>
                  <div className="cvd-privacy-card__badges">
                    <span className={`cvd-badge ${statusBadgeClass(item.status)}`}>
                      {t(`${COPY_PREFIX}.status.${item.status}`, {}, item.status)}
                    </span>
                    {item.profileSuppressed ? (
                      <span className="cvd-badge cvd-badge--warning">{t(`${COPY_PREFIX}.suppressedBadge`)}</span>
                    ) : null}
                    {isOverdue(item) ? (
                      <span className="cvd-badge cvd-badge--suspended">{t(`${COPY_PREFIX}.overdueBadge`)}</span>
                    ) : null}
                  </div>
                </div>

                <div className="cvd-run-card__stats">
                  <span>
                    {t(`${COPY_PREFIX}.table.suppressed`)}:{" "}
                    {item.profileSuppressed ? t(`${COPY_PREFIX}.yes`) : t(`${COPY_PREFIX}.no`)}
                  </span>
                  <span>
                    {t(`${COPY_PREFIX}.table.submitted`)}: {formatDateTime(item.submittedAt, locale)}
                  </span>
                </div>
              </div>

              <div className="cvd-run-card__actions">
                <Link className="cvd-btn cvd-btn--primary" to={`${basePath}/${item.id}`}>
                  {t(`${COPY_PREFIX}.openRequest`)}
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}
